class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Initialize option availability based on first selected option
    this.updateOptionAvailability();
    
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      
      // Update availability when first option changes
      this.updateOptionAvailability();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateOptionAvailability() {
    const variantsScript = this.querySelector('[data-all-variants]');
    if (!variantsScript) return;
    
    const allVariants = JSON.parse(variantsScript.textContent);
    if (!allVariants || allVariants.length === 0) return;
    
    const optionGroups = Array.from(this.querySelectorAll('fieldset, .product-form__input--dropdown'));
    
    // Get selected value for option 1 (first option group)
    const firstOptionGroup = optionGroups[0];
    if (!firstOptionGroup) return;
    
    const firstSelectedInput = firstOptionGroup.querySelector('input[type="radio"]:checked') || 
                               firstOptionGroup.querySelector('select option[selected]');
    
    if (!firstSelectedInput) return;
    
    const firstOptionValue = firstSelectedInput.value || firstSelectedInput.textContent.trim();
    
    // Filter variants that match the first option
    const matchingVariants = allVariants.filter(variant => {
      return variant.options && variant.options[0] === firstOptionValue;
    });
    
    // For each subsequent option group, check availability
    for (let i = 1; i < optionGroups.length; i++) {
      const optionGroup = optionGroups[i];
      const optionInputs = optionGroup.querySelectorAll('input[type="radio"], select option');
      
      optionInputs.forEach(input => {
        const optionValue = input.value || input.textContent.trim();
        let isAvailable = false;
        
        // Check if there's any available variant with this option value for the selected first option
        matchingVariants.forEach(variant => {
          if (variant.options && variant.options[i] === optionValue && variant.available) {
            isAvailable = true;
          }
        });
        
        // Update disabled state (CSS class only — HTML disabled attribute intentionally omitted so unavailable variants remain clickable)
        if (input.tagName === 'INPUT') {
          const label = input.closest('label') || input.nextElementSibling?.closest('label');
          if (isAvailable) {
            input.classList.remove('disabled');
            if (label) label.classList.remove('disabled');
          } else {
            input.classList.add('disabled');
            if (label) label.classList.add('disabled');
          }
        } else if (input.tagName === 'OPTION') {
          input.disabled = !isAvailable;
          const select = input.closest('select');
          if (select && select.selectedOptions[0] === input && !isAvailable) {
            // If current selection becomes unavailable, select first available option
            const firstAvailable = Array.from(select.options).find(opt => !opt.disabled);
            if (firstAvailable) {
              select.value = firstAvailable.value;
              Array.from(select.options).forEach(opt => opt.removeAttribute('selected'));
              firstAvailable.setAttribute('selected', 'selected');
            }
          }
        }
      });
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  get allOptionsSelected() {
    const allOptionGroups = this.querySelectorAll('fieldset, .product-form__input--dropdown');
    if (allOptionGroups.length === 0) return true; // No options means product has only default variant
    
    let allSelected = true;
    
    allOptionGroups.forEach(group => {
      // Check for radio buttons (fieldset)
      const checkedRadio = group.querySelector('input[type="radio"]:checked');
      // Check for select dropdowns
      const select = group.querySelector('select');
      const selectedOption = select ? select.querySelector('option[selected]') : null;
      
      if (!checkedRadio && !selectedOption) {
        allSelected = false;
      }
    });
    
    return allSelected;
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        
        // Check if button should be disabled on initial load (for products with variants)
        // Use requestAnimationFrame to ensure custom elements are fully registered
        requestAnimationFrame(() => {
          this.checkInitialButtonState();
        });
        
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        requestAnimationFrame(() => {
          this.initGalleryFilter();
        });

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          this.updateVariantMedia();
        }
      }

      checkInitialButtonState() {
        if (!this.productForm || !this.productVariantSelects) return;
        
        // Check if toggleSubmitButton method exists
        if (typeof this.productForm.toggleSubmitButton !== 'function') return;
        
        const requiresVariantSelection = this.productForm.querySelector('[data-requires-variant-selection]')?.dataset.requiresVariantSelection === 'true';
        if (requiresVariantSelection) {
          const allOptionsSelected = this.productVariantSelects.allOptionsSelected;
          const variantIdInput = this.productForm.querySelector('.product-variant-id');
          const hasVariantId = variantIdInput && variantIdInput.value && variantIdInput.value !== '';
          
          if (!allOptionsSelected || !hasVariantId) {
            this.productForm.toggleSubmitButton(true);
          }
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          // Save currently selected values before updating
          const currentSelections = {};
          const currentSelectors = this.variantSelectors;
          
          // Get all selected radio inputs
          currentSelectors.querySelectorAll('input[type="radio"]:checked').forEach(input => {
            const name = input.name;
            const value = input.value;
            if (!currentSelections[name]) {
              currentSelections[name] = value;
            }
          });
          
          // Get all selected dropdown options
          currentSelectors.querySelectorAll('select').forEach(select => {
            const selectId = select.id || select.name;
            if (select.value) {
              currentSelections[selectId] = select.value;
            }
          });
          
          // Pre-select options in the new HTML BEFORE it's inserted into DOM
          const preSelectOptions = (newContent) => {
            // newContent is the productVariantSelects element from server HTML
            Object.entries(currentSelections).forEach(([name, value]) => {
              try {
                // Select radio buttons
                const input = newContent.querySelector(`input[name="${name}"][value="${CSS.escape(value)}"]`);
                if (input && input.type === 'radio') {
                  input.setAttribute('checked', 'checked');
                  // Update legend
                  const legend = input.closest('fieldset')?.querySelector('legend [data-selected-value]');
                  if (legend) {
                    legend.textContent = value;
                  }
                }
                
                // Select dropdown options
                const select = newContent.querySelector(`select[id="${CSS.escape(name)}"], select[name="${CSS.escape(name)}"]`);
                if (select) {
                  const option = Array.from(select.options).find(opt => opt.value === value);
                  if (option) {
                    select.value = value;
                    // Update selected attribute
                    Array.from(select.options).forEach(opt => opt.removeAttribute('selected'));
                    option.setAttribute('selected', 'selected');
                  }
                }
              } catch (e) {
                // Ignore errors
              }
            });
          };
          
          // Post-process callback to update reference
          const updateReference = (newNode) => {
            this.productVariantSelects = newNode;
          };
          
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            [preSelectOptions, ...this.preProcessHtmlCallbacks],
            [updateReference, ...this.postProcessHtmlCallbacks]
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);

          // this.pickupAvailability?.update(variant);
          
          const requiresVariantSelection = this.productForm?.querySelector('[data-requires-variant-selection]')?.dataset.requiresVariantSelection === 'true';
          
          // Only update option values if we have a variant and need to update availability
          // Skip updating if user is just selecting variants on the same product to preserve selections
          if (variant) {
            // Only update if HTML has different option availability than current
            // For now, skip updateOptionValues entirely to preserve user selections
            // this.updateOptionValues(html); // DISABLED - causes selections to reset
          }
          
          if (!variant) {
            this.setUnavailable();
            // Check if all options are selected at this point
            const allOptionsSelected = this.productVariantSelects?.allOptionsSelected ?? false;
            // If variant selection is required and not all options are selected, keep button disabled
            if (requiresVariantSelection && !allOptionsSelected) {
              this.productForm?.toggleSubmitButton(true, "wybierz wariant");
            }
            // If all options are selected but variant is null, it might be an invalid combination
            if (requiresVariantSelection && allOptionsSelected && !variant) {
              this.updateVariantInputs(null);
            }
            return;
          }
          
          // Only update variant inputs if we have a valid variant
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDetails(variant?.id);
          // Inserted logic for pickupAvailability after updateVariantDetails
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            this.updateVariantMedia(html);
          }

          this.updateMedia(variant?.featured_media?.id);

          // Additionally filter gallery media by image alt tags for the selected variant
          // Example tag convention in alt: "#Extras_Roller" where "Roller" is variant option value.
          // Images without any "#" tag are treated as shared and always shown.
          if (variant) {
            this.filterMediaByAlt(variant);
          }

          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          // Check if all options are selected NOW (after variant is selected)
          const allOptionsSelected = this.productVariantSelects?.allOptionsSelected ?? true;
          
          const submitBtn = this.productForm?.querySelector('[type="submit"]');
          const notifyBtn = this.productForm?.closest('.product-form__buttons')?.querySelector('.gw-button-widget')
            || this.productForm?.querySelector('.gw-button-widget')
            || this.productForm?.parentElement?.querySelector('.gw-button-widget');
          if (submitBtn?._hideTimeout) {
            clearTimeout(submitBtn._hideTimeout);
            submitBtn._hideTimeout = null;
          }
          if (submitBtn?._showTimeout) {
            clearTimeout(submitBtn._showTimeout);
            submitBtn._showTimeout = null;
          }
          if (requiresVariantSelection && !allOptionsSelected) {
            // No variant selected — show "wybierz wariant", hide notify-me
            this.productForm?.toggleSubmitButton(true, "wybierz wariant");
            submitBtn?.style.setProperty('display', '');
            if (notifyBtn) notifyBtn.style.setProperty('display', 'none');
          } else {
            // All options selected — show/hide based on variant availability
            if (variant && variant.available !== false) {
              // Available — show our button, hide notify-me
              this.productForm?.toggleSubmitButton(false);
              if (notifyBtn) notifyBtn.style.setProperty('display', 'none');
              submitBtn._showTimeout = setTimeout(() => {
                submitBtn.style.setProperty('display', '');
                submitBtn._showTimeout = null;
              }, 400);
            } else {
              // Sold out — hide our button, show notify-me
              submitBtn.style.setProperty('opacity', '0');
              submitBtn.style.setProperty('pointer-events', 'none');
              submitBtn._hideTimeout = setTimeout(() => {
                submitBtn.style.setProperty('display', 'none');
                submitBtn.style.removeProperty('opacity');
                submitBtn.style.removeProperty('pointer-events');
                submitBtn._hideTimeout = null;
              }, 600);
              if (notifyBtn) notifyBtn.style.removeProperty('display');
            }
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }


      /**
       * Filters gallery & thumbs by rebuilding Swiper slides based on image alt tags.
       * Convention: in img alt używamy tagów typu "#Extras_Roller" gdzie część po "_" (Roller)
       * odpowiada pierwszej opcji wybranego wariantu.
       */
      initGalleryFilter() {
        if (!this.productMediaGallery) return;

        const galleryWrapper = this.productMediaGallery.querySelector('.swiper--product-gallery .swiper-wrapper');
        const thumbsWrapper = this.productMediaGallery.querySelector('.swiper--product-thumbs .swiper-wrapper');
        if (!galleryWrapper) return;

        const allSlides = Array.from(galleryWrapper.querySelectorAll('.swiper-slide'));
        const hasTaggedMedia = allSlides.some(slide => slide.querySelector('img[alt]')?.alt.includes('#'));
        if (!hasTaggedMedia) return;

        // Cache pełnego zestawu slajdów (współdzielony z filterMediaByAlt)
        if (!this._allGallerySlides) {
          this._allGallerySlides = allSlides.map(slide => ({
            mediaId: slide.dataset.mediaId,
            alt: slide.querySelector('img[alt]')?.alt || '',
            html: slide.outerHTML,
          }));

          if (thumbsWrapper) {
            this._allThumbSlides = Array.from(thumbsWrapper.querySelectorAll('.swiper-slide')).map(slide => ({
              mediaId: slide.dataset.mediaId,
              html: slide.outerHTML,
            }));
          }
        }

        // Na starcie pokazuj tylko zdjęcia wspólne (nietagowane)
        const sharedIds = new Set(
          this._allGallerySlides.filter(({ alt }) => !alt.includes('#')).map(({ mediaId }) => mediaId)
        );

        galleryWrapper.innerHTML = this._allGallerySlides
          .filter(({ mediaId }) => sharedIds.has(mediaId))
          .map(({ html }) => html)
          .join('');

        if (thumbsWrapper && this._allThumbSlides) {
          thumbsWrapper.innerHTML = this._allThumbSlides
            .filter(({ mediaId }) => sharedIds.has(mediaId))
            .map(({ html }) => html)
            .join('');
        }

        // Jeśli Swiper jest już zainicjowany — odśwież; jeśli nie — zainicjuje się sam na przefiltrowanym DOM
        this.productMediaGallery.querySelectorAll('swiper-product-gallery, swiper-product-thumbs').forEach(el => {
          if (el.instance) {
            el.instance.update();
            el.instance.slideTo(0);
          }
        });
      }

      filterMediaByAlt(variant) {
        if (!this.productMediaGallery || !variant || !variant.options || !variant.options.length) return;

        const selectedOptionValue = variant.options[0];
        if (!selectedOptionValue) return;

        const normalize = (str) =>
          String(str)
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-');

        const valueNorm = normalize(selectedOptionValue);
        const tagRegex = new RegExp(`#([^\\s_]+_)?${valueNorm}\\b`);

        const galleryWrapper = this.productMediaGallery.querySelector('.swiper--product-gallery .swiper-wrapper');
        const thumbsWrapper = this.productMediaGallery.querySelector('.swiper--product-thumbs .swiper-wrapper');
        if (!galleryWrapper) return;

        // Cache pełnego zestawu slajdów tylko raz
        if (!this._allGallerySlides) {
          this._allGallerySlides = Array.from(galleryWrapper.querySelectorAll('.swiper-slide')).map((slide) => {
            const img = slide.querySelector('img[alt]');
            return {
              mediaId: slide.dataset.mediaId,
              alt: img?.alt || '',
              html: slide.outerHTML,
            };
          });

          if (thumbsWrapper) {
            this._allThumbSlides = Array.from(thumbsWrapper.querySelectorAll('.swiper-slide')).map((slide) => ({
              mediaId: slide.dataset.mediaId,
              html: slide.outerHTML,
            }));
          }
        }

        if (!this._allGallerySlides || !this._allGallerySlides.length) return;

        // First pass: find matching tagged images
        const matchingIds = new Set();
        let hasTaggedMedia = false;

        this._allGallerySlides.forEach(({ mediaId, alt }) => {
          if (!alt.includes('#')) return;
          hasTaggedMedia = true;
          if (tagRegex.test(normalize(alt))) {
            matchingIds.add(mediaId);
          }
        });

        // When matching tagged images exist → show only those (skip generic/untagged).
        // When no match found → fall back to untagged (shared) images.
        const wantedMediaIds = new Set();
        if (hasTaggedMedia && matchingIds.size > 0) {
          matchingIds.forEach(id => wantedMediaIds.add(id));
        } else {
          this._allGallerySlides.forEach(({ mediaId, alt }) => {
            if (!alt.includes('#')) wantedMediaIds.add(mediaId);
          });
        }

        const hasVariantMatch = matchingIds.size > 0;
        // Jeśli są tagowane media, ale żadne nie pasuje -> pokaż tylko nietagowane (wspólne/modelowe)

        // Odtwórz HTML dla galerii głównej
        const galleryHtml = this._allGallerySlides
          .filter(({ mediaId }) => wantedMediaIds.has(mediaId))
          .map(({ html }) => html)
          .join('');
        galleryWrapper.innerHTML = galleryHtml;

         // Odtwórz HTML dla miniaturek (jeśli istnieją)
        if (thumbsWrapper && this._allThumbSlides && this._allThumbSlides.length) {
          const thumbsHtml = this._allThumbSlides
            .filter(({ mediaId }) => wantedMediaIds.has(mediaId))
            .map(({ html }) => html)
            .join('');
          thumbsWrapper.innerHTML = thumbsHtml;

          const allThumbSlides = thumbsWrapper.querySelectorAll('.swiper-slide');
          const firstThumb = allThumbSlides[0];
          if (firstThumb) {
            // Usuń ewentualne stare klasy aktywne
            thumbsWrapper
              .querySelectorAll('.swiper-slide-thumb-active, [aria-current="true"]')
              .forEach(slide => {
                slide.classList.remove('swiper-slide-thumb-active');
                slide.removeAttribute('aria-current');
              });

            firstThumb.classList.add('swiper-slide-thumb-active');
            firstThumb.setAttribute('aria-current', 'true');
          }
        }

        // Zaktualizuj instancje Swipera, żeby przeliczyły liczbę slajdów / paginację
        this.productMediaGallery.querySelectorAll('swiper-product-gallery, swiper-product-thumbs').forEach(
          (swiperElement) => {
            if (swiperElement.instance) {
              swiperElement.instance.update();
              swiperElement.instance.slideTo(0);
            } else if (typeof swiperElement.init === 'function') {
              swiperElement.init();
            }
          },
        );

        // Po przefiltrowaniu miniaturek przelicz max-height wrappera,
        // korzystając z metody zdefiniowanej w InstanceSwiperProductThumbs
        const thumbsElement = this.productMediaGallery.querySelector('swiper-product-thumbs');
        if (thumbsElement && typeof thumbsElement.setThumbsWrapperHeightToAuto === 'function') {
          // Pierwsze wywołanie tuż po przebudowaniu DOM
          thumbsElement.setThumbsWrapperHeightToAuto();
          // Drugie po jednym „cyklu” reflow, gdy przeliczą się wysokości obrazków/slidera
          requestAnimationFrame(() => {
            thumbsElement.setThumbsWrapperHeightToAuto();
          });
        }
      }


      updateVariantMedia(html) {
        // Get selected variant from current DOM or from html parameter
        const variant = html ? this.getSelectedVariant(html) : this.getSelectedVariant(this);

        if (!variant || !variant.options || variant.options.length === 0) {
          // If no variant found, show all media (fallback)
          this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => {
            element.classList.remove('hidden');
          });
          return;
        }

        // Generate handle from first option of selected variant
        const select_variant_options_handle = this.generateHandle(variant.options[0]);

        // Hide all media that have a data-media-group
        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => {
          const mediaGroup = element.getAttribute('data-media-group');
          // Media with empty data-media-group are always visible (shared media)
          if (mediaGroup && mediaGroup !== '') {
            element.classList.add('hidden');
          }
        });

        // Show media that match the selected variant's option1 handle
        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => {
          element.classList.remove('hidden');
        });

        // Also show media with empty data-media-group (shared media)
        this.productMediaGallery.querySelectorAll('[data-media-group=""]').forEach(element => {
          element.classList.remove('hidden');
        });

        // Update Swiper instances to recalculate layout
        this.productMediaGallery.querySelectorAll('swiper-product-gallery, swiper-product-thumbs').forEach(swiperElement => {
          if (swiperElement.instance) {
            swiperElement.instance.update(); // Update the Swiper instance
          } else {
            swiperElement.init(); // If the instance is not available, reinitialize
          }
        });

        // Slide to first visible slide if current is hidden
        const mainGallery = this.productMediaGallery.querySelector('swiper-product-gallery');
        if (mainGallery?.instance) {
          const currentSlide = mainGallery.instance.slides[mainGallery.instance.activeIndex];
          if (currentSlide && currentSlide.classList.contains('hidden')) {
            // Find first visible slide
            const visibleSlideIndex = mainGallery.instance.slides.findIndex(
              slide => !slide.classList.contains('hidden')
            );
            if (visibleSlideIndex !== -1) {
              mainGallery.instance.slideTo(visibleSlideIndex);
            }
          }
        }
      }

      generateHandle(text) {
        // Simple handleize function - converts text to lowercase, replaces spaces with hyphens, removes special chars
        return String(text)
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-');
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          const requiresVariantSelection = input?.dataset.requiresVariantSelection === 'true';
          
          if (variantId) {
            input.value = variantId;
            if (requiresVariantSelection) {
              input.disabled = false;
            }
          } else {
            if (requiresVariantSelection) {
              input.value = "";
              input.disabled = true;
            } else {
          input.value = variantId ?? "";
            }
          }
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }


      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

      // If variant selection is required and button starts disabled, show helper text
      // Skip if product has no available variants at all — button should show "Sold out"
      const requiresVariantSelection =
        this.submitButton.dataset.requiresVariantSelection === "true";
      const productAvailable =
        this.submitButton.dataset.productAvailable !== "false";
      if (
        requiresVariantSelection &&
        this.submitButton.hasAttribute("disabled") &&
        productAvailable
      ) {
        this.toggleSubmitButton(true, "wybierz wariant");
      }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute("aria-disabled") === "true"
        )
          return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});